import React, { useState } from 'react';
import { injectIntl } from 'react-intl';
import { i18nIntl } from '../../lib/ep.js';
import { Route, HashRouter as Router, Switch } from 'react-router-dom';

import BuilderCanvas from '../builder/BuilderCanvas';
import BuilderMain from '../builder/BuilderMain.js';
import BuilderSidebar from '../builder/BuilderSidebar';
import DynamicFormBuilder from '../builder/DynamicForm.js';
import Input from '../builder/Input';
import MediaUploader from '../builder/MediaUploader.js';
import Model from '../builder/Model';
import { NavigationTabsBuilder } from '../builder/NavigationTabs';
import RichEditor from '../builder/RichEditor';
import Tooltip from '../builder/Tooltip.js';

import Layout from '../front/Layout';
import StageFormClient from '../front/StageFormClient.js';

import transferPlaceholder from '../../hoc/transferPlaceholder';
import StageFinalMessages from '../games/StageFinalMessages.js';

import BuilderSubSectionTitle from './BuilderSubSectionTitle.js';
import { AutoPostImageUploader } from '../builder/ImageUploader.js';
import TextArea from '../builder/TextArea.js';
import Select from '../builder/Select.js';

const InputPlaceHolder = injectIntl(transferPlaceholder(Input));
const RichPlaceHolder = injectIntl(transferPlaceholder(RichEditor));

const StageForm = ({ stage, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);
	const [ errors, setErrors ] = useState({});

	const changeForm = (v) => {
		change('form',v);
	}

	const avail_locales = [];
	if(window.langs != undefined) {
		window.langs.map((loc) => {
			avail_locales.push(loc.I18nPromotion.lang);
		})
	}
	const only = [
		'field',
		'dropdown',
		'checkbox',		
		'date',
		'autocomplete',
		'optin',
		'radio_button',
		'signature',
		'attach',
	];
	if (window.features?.includes('multi_checkbox')) {
		
	}
	only.push('multi_checkbox');

	return (
		<>
			<BuilderSidebar onSave={props.saveStage} errors={errors}>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<article>
					<DynamicFormBuilder
						only={only}
						form={stage.form ?? []}
						has_translation={true}
						locales={avail_locales}
						onChange={changeForm}
					/>
				</article>

				<article class='gui_not_in_top_huge'>
					<BuilderSubSectionTitle
						title="Contents"
						icon='knk-profile'
					/>

					<Model model={stage} change={change}>
						<MediaUploader
							imageField="direct_image"
							videoField="main_video"
							image={stage.full_direct_image}
							video={(stage.main_video) ? stage.main_video.url : null}
						>Upload media</MediaUploader>

						<RichPlaceHolder
							name="direct_description"
							placeholder="Registering the participation..."
							label_props={{
								tooltip:<Tooltip>Enter a blank space to delete the text</Tooltip>,
							}}
						>Description</RichPlaceHolder>

						<InputPlaceHolder
							placeholder="Continue"
							name="main_button"
							autotranslate={false}
							_class={{ size:40 }}
						>{i18n("Label for '%s' button").replace('%s', i18n("Continue"))}</InputPlaceHolder>
					</Model>
				</article>
			</BuilderSidebar>

			<BuilderCanvas fit_to_window="content">
				<Layout
					promo={props.promotion}
					is_builder={true}
					page="quiz"
					menus={props.promoModel.Custom__Menus}
					layout_extra_classes="is_new_game"
				>
					<div style={{width:'100%'}}>
						{(Object.keys(errors).length == 0) && (
							<StageFormClient
								stage={{ Stage:stage }}
								field_config={{
									signature:{
										auto_url:`/promotions/auto_img/${stage.promotion_id}`,
										auto_type:"stages",
										is_builder:true,
									}
								}}
								promotion={{ Promotion:props.promotion }}
								is_builder={true}
							/>
						)}
					</div>
				</Layout>
			</BuilderCanvas>
		</>
	)
};

const FinalPage = ({ stage, change, ...props }) => {
	const [ final_case, setFinalCase ] = useState('success');
	const i18n = (m, c) => i18nIntl(m, props.intl, c);
	//const actual_state = (final_case !== false) ? generated_fields.find((i) => (i.id == final_case)) : null;
	stage.display_status = final_case;

	const final_modes = [
		{ k:'', v:"----" },
		{ k:'redirect', v:i18n("Redirect to final page") },
		{ k:'message', v:i18n("Show a custom message") },
	]

	return (
		<>
			<BuilderSidebar onSave={props.saveStage}>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>
				
				<Select
					name="final_mode"
					change={change}
					value={stage.final_mode}
					values={final_modes}
				>What do you want to show</Select>
				
				{stage.final_mode == 'message' && (
					<Model model={stage} change={change}>
						<RichEditor
							name="final_message"
						>Description</RichEditor>

						<AutoPostImageUploader
							name="final_image"
							auto_url={`/promotions/auto_img/${stage.promotion_id}`}
							auto_type="stages"
							image={stage.full_final_image}
						>Final image</AutoPostImageUploader>
					</Model>
				)}
				
			</BuilderSidebar>

			<BuilderCanvas is_adaptable={true} fit_to_window="content">
				<Layout
					promo={props.promotion}
					is_builder={true}
					page="quiz"
					menus={props.promoModel.Custom__Menus}
					layout_extra_classes="is_new_game"
				>
					<div>
						<StageFormClient
							stage={{ Stage:stage }}
							field_config={{
								signature:{
									auto_url:`/promotions/auto_img/${stage.promotion_id}`,
									auto_type:"stages",
									is_builder:true,
								}
							}}
							promotion={{ Promotion:props.promotion }}
							is_builder={true}
							section='SUCCESS'
						/>
					</div>
				</Layout>
			</BuilderCanvas>
		</>
	)
};

const StageFormBuilder = ({ base_route, ...props }) => {
	const basename = `/stages/${props.stage.id}/form`;
	const nav_sections = [
		{ label:"Go back", link:`/stages/${props.stage.id}/options`, icon:'knk-go-prev' },
		{ label:"Fields", link:`${basename}/settings`, icon:'knk-list' },
		{ label:"Final message", link:`${basename}/final`, icon:'knk-list' },
	];

	return (
		<BuilderMain pm={props.promoModel}>
			<Router basename={basename} hashType="noslash">
				<Switch>
					<Route exact={true} path="/settings">
						<StageForm
							change={props.change}
							is_multistage={props.is_multistage}
							intl={props.intl}
							isCorporate={props.isCorporate}
							nav_active={`/settings`}
							nav_sections={nav_sections}
							promotion={props.promotion}
							promoModel={props.promoModel}
							saveStage={props.saveStage}
							stage={props.stage}
							stage_indicator={props.stage_indicator}
						/>
					</Route>

					<Route exact={true} path="/final">
						<FinalPage
							change={props.change}
							changeMulti={props.changeMulti}
							intl={props.intl}
							nav_active={`/final`}
							nav_sections={nav_sections}
							promotion={props.promotion}
							promoModel={props.promoModel}
							saveStage={props.saveStage}
							stage={props.stage}
							stage_indicator={props.stage_indicator}
						/>
					</Route>
				</Switch>
			</Router>
		</BuilderMain>
	)
};

export default injectIntl(StageFormBuilder);